/**
 * ProductTabs Component
 * 
 * A tabbed section placed before the collapsibles in the hero area.
 * Tab 1: How to Use (3 steps)
 * Tab 2: Ingredients (5 key ingredients with SVG icons)
 * Tab 3: Clinical Results (dark blue block with stats + disclaimer)
 */

import { useState } from 'react';

const STEP_VIDEOS = [
  'https://colagea.com/cdn/shop/videos/c/vp/0313642d83e344cd8afc60671c2c0b58/0313642d83e344cd8afc60671c2c0b58.HD-1080p-3.3Mbps-57326615.mp4?v=0',
  'https://colagea.com/cdn/shop/videos/c/vp/1f2251f3a9e640ca9ad0ea5abc67889d/1f2251f3a9e640ca9ad0ea5abc67889d.HD-1080p-3.3Mbps-57326616.mp4?v=0',
  'https://colagea.com/cdn/shop/videos/c/vp/64338fde025747278cec0886b830deb1/64338fde025747278cec0886b830deb1.HD-1080p-2.5Mbps-57326617.mp4?v=0',
];

// Unique SVG icons for each ingredient
function AcetylTetrapeptideIcon() {
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
      {/* Molecular chain / peptide structure */}
      <circle cx="12" cy="12" r="3" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC20" />
      <circle cx="28" cy="12" r="3" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC20" />
      <circle cx="20" cy="28" r="3" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC20" />
      <circle cx="8" cy="28" r="2.5" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC20" />
      <circle cx="32" cy="28" r="2.5" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC20" />
      <line x1="14" y1="13" x2="26" y2="13" stroke="#0066CC" strokeWidth="1.5" />
      <line x1="12" y1="15" x2="18" y2="26" stroke="#0066CC" strokeWidth="1.5" />
      <line x1="28" y1="15" x2="22" y2="26" stroke="#0066CC" strokeWidth="1.5" />
      <line x1="10" y1="27" x2="18" y2="28" stroke="#0066CC" strokeWidth="1" strokeDasharray="2 2" />
      <line x1="22" y1="28" x2="30" y2="27" stroke="#0066CC" strokeWidth="1" strokeDasharray="2 2" />
      <circle cx="20" cy="20" r="1.5" fill="#0066CC" />
    </svg>
  );
}

function SawPalmettoIcon() {
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
      {/* Palm leaf / fan shape */}
      <path d="M20 36V20" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 20C14 14 6 10 4 8c4 4 8 10 16 12z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <path d="M20 20C26 14 34 10 36 8c-4 4-8 10-16 12z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <path d="M20 20C16 12 14 4 14 2c2 4 4 10 6 18z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <path d="M20 20C24 12 26 4 26 2c-2 4-4 10-6 18z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <path d="M20 20C10 16 4 18 2 18c4 0 10 2 18 2z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <path d="M20 20C30 16 36 18 38 18c-4 0-10 2-18 2z" fill="#16a34a20" stroke="#16a34a" strokeWidth="1.5" />
      <circle cx="20" cy="20" r="2" fill="#16a34a" />
    </svg>
  );
}

function CaffeineIcon() {
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
      {/* Caffeine molecular structure - hexagonal rings */}
      <path d="M14 10L22 10L26 17L22 24L14 24L10 17Z" stroke="#8B4513" strokeWidth="1.5" fill="#8B451310" />
      <path d="M22 10L30 10L34 17L30 24L22 24" stroke="#8B4513" strokeWidth="1.5" fill="#8B451310" />
      <line x1="14" y1="24" x2="12" y2="30" stroke="#8B4513" strokeWidth="1.5" />
      <line x1="22" y1="24" x2="24" y2="30" stroke="#8B4513" strokeWidth="1.5" />
      <circle cx="10" cy="17" r="1.5" fill="#8B4513" />
      <circle cx="26" cy="17" r="1.5" fill="#8B4513" />
      <circle cx="34" cy="17" r="1.5" fill="#8B4513" />
      <text x="18" y="19" fontSize="5" fill="#8B4513" fontWeight="bold" textAnchor="middle">N</text>
      <text x="28" y="19" fontSize="5" fill="#8B4513" fontWeight="bold" textAnchor="middle">N</text>
    </svg>
  );
}

function MentholIcon() {
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
      {/* Mint leaf with cooling lines */}
      <path d="M20 4C12 8 6 16 8 28c2 4 6 6 12 8 6-2 10-4 12-8 2-12-4-20-12-24z" fill="#10b98120" stroke="#10b981" strokeWidth="1.5" />
      <path d="M20 4v32" stroke="#10b981" strokeWidth="1" />
      <path d="M20 12c-4 2-8 6-9 10" stroke="#10b981" strokeWidth="1" />
      <path d="M20 12c4 2 8 6 9 10" stroke="#10b981" strokeWidth="1" />
      <path d="M20 20c-3 2-6 4-7 8" stroke="#10b981" strokeWidth="1" />
      <path d="M20 20c3 2 6 4 7 8" stroke="#10b981" strokeWidth="1" />
      {/* Cooling sparkle lines */}
      <line x1="2" y1="8" x2="6" y2="10" stroke="#10b981" strokeWidth="1" strokeLinecap="round" />
      <line x1="34" y1="8" x2="38" y2="6" stroke="#10b981" strokeWidth="1" strokeLinecap="round" />
      <line x1="36" y1="14" x2="38" y2="12" stroke="#10b981" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function CopperTripeptideIcon() {
  return (
    <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
      {/* Copper peptide - triple helix / DNA-like */}
      <circle cx="20" cy="20" r="6" stroke="#0066CC" strokeWidth="1.5" fill="#0066CC15" />
      <circle cx="20" cy="20" r="3" fill="#0066CC40" />
      <text x="20" y="22" fontSize="5" fill="#0066CC" fontWeight="bold" textAnchor="middle">Cu</text>
      {/* Orbital rings */}
      <ellipse cx="20" cy="20" rx="14" ry="6" stroke="#0066CC" strokeWidth="1" transform="rotate(-30 20 20)" strokeDasharray="3 2" />
      <ellipse cx="20" cy="20" rx="14" ry="6" stroke="#0066CC" strokeWidth="1" transform="rotate(30 20 20)" strokeDasharray="3 2" />
      <ellipse cx="20" cy="20" rx="14" ry="6" stroke="#0066CC" strokeWidth="1" transform="rotate(90 20 20)" strokeDasharray="3 2" />
      {/* Electron dots */}
      <circle cx="8" cy="14" r="1.5" fill="#0066CC" />
      <circle cx="32" cy="14" r="1.5" fill="#0066CC" />
      <circle cx="20" cy="6" r="1.5" fill="#0066CC" />
    </svg>
  );
}

const ingredients = [
  {
    name: 'Acetyl Tetrapeptide-3',
    description: 'Strengthens hair anchoring at the root for reduced fallout.',
    Icon: AcetylTetrapeptideIcon,
    bgColor: '#EBF1F8',
  },
  {
    name: 'Saw Palmetto',
    description: 'Natural DHT inhibitor that combats male pattern hair loss.',
    Icon: SawPalmettoIcon,
    bgColor: '#f0fdf4',
  },
  {
    name: 'Caffeine',
    description: 'Stimulates microcirculation and awakens dormant hair follicles.',
    Icon: CaffeineIcon,
    bgColor: '#fef3c7',
  },
  {
    name: 'Menthol',
    description: 'Provides a cooling sensation and stimulates the scalp.',
    Icon: MentholIcon,
    bgColor: '#ecfdf5',
  },
  {
    name: 'Copper Tripeptide-1',
    description: 'Promotes cellular regeneration and reactivates follicles for denser regrowth.',
    Icon: CopperTripeptideIcon,
    bgColor: '#EBF1F8',
  },
];

const clinicalStats = [
  { value: '90%', label: 'report visibly thicker hair', icon: '📈' },
  { value: '86%', label: 'reduction in hair loss', icon: '🛡️' },
  { value: '82%', label: 'new hair growth observed', icon: '🌱' },
  { value: '82%', label: 'would recommend to a friend', icon: '⭐' },
];

export function ProductTabs() {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { num: '01', label: 'How to Use' },
    { num: '02', label: 'Ingredients' },
    { num: '03', label: 'Clinical Results' },
  ];

  return (
    <div 
      className="mt-3 mb-4 border border-gray-200 rounded-2xl bg-white overflow-hidden mx-0 md:mx-0"
      style={{ scrollMarginTop: '20px' }}
    >
      {/* Tab Headers */}
      <div className="flex border-b border-gray-100">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className="flex-1 py-3 px-2 sm:px-4 text-center transition-all duration-200 relative"
            style={{
              background: activeTab === idx ? '#ffffff' : 'rgba(0, 102, 204, 0.02)',
              borderBottom: activeTab === idx ? '2px solid #0066CC' : '2px solid transparent',
            }}
          >
            <span 
              className="text-[10px] mr-1 opacity-40"
              style={{ color: activeTab === idx ? '#004C99' : '#999' }}
            >
              {tab.num}
            </span>
            <span 
              className="text-xs font-semibold tracking-wide"
              style={{ color: activeTab === idx ? '#004C99' : '#999' }}
            >
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="px-3 sm:px-5 py-4 sm:py-5">
        {/* Tab 1: How to Use */}
        {activeTab === 0 && (
          <div className="animate-[fadeSlideIn_0.3s_ease-out]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">How to Use</h3>
              <span className="text-[10px] sm:text-xs font-bold text-[#0066CC] bg-[#0066CC]/5 px-2.5 py-1 rounded-full tracking-wide">
                3 SIMPLE STEPS
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              3 steps. Twice a week. Designed for simplicity. Backed by clinical expertise.
            </p>

            {/* 3 Step Cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { step: 1, title: 'Prepare', desc: 'Start on clean, dry hair.' },
                { step: 2, title: 'Attach Device', desc: 'Screw the micro-infusion applicator onto the serum vial until secure.' },
                { step: 3, title: 'Apply', desc: 'Press gently on targeted areas of the scalp.' },
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center text-center">
                  <div className="w-full aspect-square rounded-xl overflow-hidden bg-gray-100 mb-2">
                    <video
                      src={STEP_VIDEOS[item.step - 1]}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-[10px] font-bold text-[#0066CC] tracking-wider uppercase mb-0.5">
                    Step {item.step}
                  </span>
                  <span className="text-xs font-semibold text-gray-900 leading-tight">{item.title}</span>
                  <span className="text-[10px] text-gray-500 leading-tight mt-0.5 hidden sm:block">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 2: Ingredients */}
        {activeTab === 1 && (
          <div className="animate-[fadeSlideIn_0.3s_ease-out]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">What's Inside</h3>
              <span className="text-[10px] sm:text-xs font-bold text-[#0066CC] bg-[#0066CC]/5 px-2.5 py-1 rounded-full tracking-wide">
                5 KEY ACTIVES
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              A scientifically formulated blend of peptides, botanicals, and bioactive compounds targeting hair loss at the root.
            </p>

            {/* Ingredients Grid */}
            <div className="space-y-2">
              {ingredients.map((ing, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-2.5 rounded-xl transition-colors"
                  style={{ backgroundColor: ing.bgColor }}
                >
                  <div className="flex-shrink-0">
                    <ing.Icon />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-gray-900 block">{ing.name}</span>
                    <span className="text-[10px] sm:text-xs text-gray-500 leading-tight block">{ing.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Clinical Results */}
        {activeTab === 2 && (
          <div className="animate-[fadeSlideIn_0.3s_ease-out]">
            {/* Dark blue results block */}
            <div className="bg-[#0a1628] rounded-2xl p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm sm:text-base font-semibold">Clinically Proven Results</h3>
                <span className="text-[10px] sm:text-xs font-bold text-white/70 bg-white/10 px-2.5 py-1 rounded-full tracking-wide">
                  90-DAY CLINICAL STUDY
                </span>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                {clinicalStats.map((stat, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-lg">{stat.icon}</span>
                    <div>
                      <span className="text-2xl sm:text-3xl font-black text-[#4da6ff] block leading-none font-heading">{stat.value}</span>
                      <span className="text-[10px] sm:text-xs text-white/70 leading-tight block mt-1">Of participants {stat.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <p className="text-[10px] text-white/40 italic border-t border-white/10 pt-3">
                *Based on a 90-day randomized controlled trial conducted by an independent dermatology research institute with 200+ male participants aged 25-65.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductTabs;
